import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { cn } from './cn';

/**
 * ⓘ — the explanation, one hover or tap away.
 *
 * Screens used to open on a paragraph under every heading saying what the
 * section is for. A first-time user had to read all of it before seeing any
 * data, and an everyday user read past it every time (owner's review,
 * 14 Oct 2026). The heading now says what it is; the why sits here.
 *
 * Hover opens it with a mouse, a tap toggles it on a phone, keyboard focus
 * opens it and Escape or a tap elsewhere closes it. Drawn in a portal with
 * fixed positioning, so a card's `overflow: hidden` cannot clip it, and
 * flipped to stay on screen near an edge.
 */
export function InfoTip({ children, label = 'More information', className }: {
  children: ReactNode;
  /** What a screen reader announces for the button. */
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const pointer = useRef<string>('mouse');
  const id = useId();

  // Below the icon, kept inside the viewport with an 8px margin.
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    // The visible width: innerWidth counts a scrollbar the panel would sit under.
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const width = Math.min(320, vw - 16);
    const left = Math.max(8, Math.min(r.left + r.width / 2 - width / 2, vw - width - 8));
    const height = panel.current?.offsetHeight ?? 0;
    const below = r.bottom + 6;
    const top = below + height > window.innerHeight - 8 && r.top - height - 6 > 8 ? r.top - height - 6 : below;
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!button.current?.contains(t) && !panel.current?.contains(t)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    const shut = () => setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        data-infotip=""
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onPointerDown={(e) => { pointer.current = e.pointerType; }}
        onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(true); }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpen(false); }}
        // A tap toggles; a click with a mouse that is already hovering keeps it open.
        onClick={() => setOpen((o) => (pointer.current === 'mouse' ? true : !o))}
        onFocus={() => setOpen(true)}
        onBlur={(e) => { if (!panel.current?.contains(e.relatedTarget as Node)) setOpen(false); }}
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full align-middle text-neutral-400',
          'hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          // A thumb-sized target on a phone, the icon the same size.
          'max-md:h-8 max-md:w-8',
          className,
        )}
      >
        <Info aria-hidden className="h-4 w-4" />
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panel}
          id={id}
          role="tooltip"
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: Math.min(320, (document.documentElement.clientWidth || window.innerWidth) - 16) }}
          className="z-[1000] rounded-card border border-neutral-200 bg-white px-3 py-2.5 text-small font-normal normal-case leading-relaxed tracking-normal text-neutral-700 shadow-lg"
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
